/* THE SIGN-IN SCREEN — and the two ways an account can be unusable
 *
 * PHASE 3, STEP 6. Like `idb.js`, `auth.js`, `mapper.js` and
 * `remote.js`, this file is NEW CODE rather than moved code:
 * `scripts/extract.mjs` does not generate it. Everything else in src/
 * except those is regenerated from the HTML source, so a hand edit there
 * is silently lost — see BUILD_NOTES.md.
 *
 * ── WHY THIS EXISTS ────────────────────────────────────────────────
 * Step 4 put the vault in Postgres. `anon` has no grant on `profiles`
 * or `holdings`, so since then a browser with no session can neither
 * read nor write a single row — the app has been browse-only. This is
 * the screen that ends that.
 *
 * ── ⚠ SIGN-IN ONLY, AND THE SCREEN SAYS SO OUT LOUD ────────────────
 * The project requires a confirmed email address (`mailer_autoconfirm`
 * is false — measured, and it is the CORRECT setting: the handoff's
 * dormancy and cancellation warnings depend on being able to reach
 * people). Supabase's built-in mailer is rate-limited and explicitly
 * not for production, and custom SMTP is Phase 5.
 *
 * So a "Create account" button here would mint an account that CANNOT
 * SIGN IN, and the person would have no way to tell why. Jesse's call,
 * 2026-08-08: *"sign-up is honest about the email it can't send. A
 * create-account button producing an unusable account is worse than not
 * offering one."* The screen therefore explains the situation and
 * offers no button at all.
 *
 * ── ✅ THAT CONDITION ENDED 2026-09-09 — PHASE 5, STEP 3 ────────────
 * Custom SMTP went on by his hand (step 2), so the mail CAN be sent and
 * the 2026-08-08 reasoning now points the other way: the honest screen
 * is the one with the button. The "No account yet?" card is a real
 * create-account form; a `confirm` screen says where the mail went and
 * offers to send it again; and a confirmation link returning with
 * tokens in the URL fragment is consumed at boot by `bootFromUrl`,
 * which is also the landing Google will reuse in step 4. ⚠ The card's
 * heading is unchanged on purpose — `reload-check` reads "No account
 * yet?" as the fingerprint of the signed-out screen.
 *
 * ── ⚠ AND THE OTHER DEAD END: SIGNED IN, NO PROFILE ────────────────
 * Raised by Jesse, 2026-08-08, and it is the sharper of the two.
 *
 * Every read and write in `remote.js` assumes the `profiles` row the
 * signup trigger creates. If it is not there — the trigger failed, the
 * row was deleted, an account predates the trigger — then `remote.js`
 * throws `remote.no_profile_row` and, before this file existed, that
 * surfaced as a console error nobody would ever see. With a sign-in
 * screen it becomes something worse: **signed in, app visibly broken,
 * nothing on screen saying why and no way out.**
 *
 * Jesse: *"a collector seeing a broken app with no way to fix it, on a
 * UI that can't tell them what's wrong, is exactly the failure I'd
 * never diagnose myself."*
 *
 * So it gets a real screen: it names the fault as ours, says plainly
 * that nothing they saved is affected, prints the account id for
 * support, and ALWAYS offers sign-out so nobody is trapped on it.
 *
 * ── ⚠ THE "REPAIR" BUTTON: REMOVED 2026-08-08, BACK 2026-09-11 ─────
 * One was built first, and MEASURING it is what removed it. `profiles`
 * has no INSERT policy for the owner, so the repair failed with 403
 * `42501` — a button that produced a raw permissions error would have
 * been worse than no button, which is the same reasoning that kept
 * "create account" off the sign-in screen until step 3.
 *
 * A policy permissive enough to allow the repair CANNOT BE ADDED
 * SAFELY from here: RLS constrains which ROW you may insert, not which
 * COLUMNS you may set, so `with check (auth.uid() = id)` would let
 * exactly the users in this broken state insert themselves a row with
 * `subscription_status: 'active'`. Measured alongside it: a user can
 * neither insert nor delete their own profile row, so this state
 * cannot be self-inflicted — it takes a trigger failure or an admin.
 *
 * ✅ PHASE 5 STEP 5, HIS RULING 2026-09-11 — SHAPE A. The fix is
 * `db/011`'s `repair_profile()`: a SECURITY DEFINER database function
 * with NO argument that inserts the signup trigger's three columns for
 * `auth.uid()` and lets the table choose every other default. The
 * button below calls it through `remote.repairProfile()`, which
 * re-reads the row rather than trusting the status, and then runs the
 * ordinary session boot. Nothing on this side chooses a column. The
 * ledger's earlier shape — a Pages Function holding the secret key —
 * was not taken (safety rule 4: the boundary lives in the database).
 *
 * ── ⚠ NO NEW CSS IS POSSIBLE HERE ──────────────────────────────────
 * `npm run extract` asserts `src/style.css` is byte-identical to the
 * source HTML's <style> block, and that source is the archived original
 * which must not be edited. Everything below is built from classes that
 * already exist plus inline styles. That is a constraint, not a
 * preference — adding a rule to style.css turns a passing gate red.
 */

import { OTHERS_NOT_SIGNED_OUT_MESSAGE, PENDING_MESSAGE, beginGoogleSignIn, getSession, isRecoveryPending, isSignedIn, landFromUrl, requestPasswordReset, resendConfirmation, setNewPassword, signIn, signOut, signUp } from './auth.js'
import { loadProfile, loadPublic, sweepPendingBrands } from './community.js'
import { loadCustom } from './custom.js'
import { abortAllUploads, sweepPendingPhotos } from './migrate.js'
import { xhrUpload } from './photo-xhr.js'
import { render } from './render.js'
import { applyReading } from './suggest.js'
import { PROFILE_KEY, repairProfile } from './remote.js'
import { $, load, state, toast } from './state.js'

/* Screens a signed-out visitor cannot open.
 *
 * ⚠ `catalog` (Resources) IS HERE, AND IT IS THE ONE ENTRY NOT JUSTIFIED
 * BY DATA ACCESS. Jesse's call, 2026-08-09, in the Step 7 walkthrough:
 * the Friedberg catalog ships in the bundle and gating it protects
 * nothing, but Session A already decided a visitor must sign in even to
 * view a PUBLIC profile — so leaving Resources open made the app
 * half-gated, which reads as an oversight rather than a decision. It is
 * gated for consistency, and the reason is written down here so nobody
 * later "fixes" it back on security grounds it never rested on.
 *
 * `learn` is deliberately NOT here, also Jesse's call: it is explanatory
 * content, there is nothing to hide, and it leaves a signed-out visitor
 * somewhere to land that is not a login form. That asymmetry is now the
 * only one, and it is intentional.
 *
 * `detail` is not listed because it is not reachable by a nav tap — it
 * is only ever opened from a catalog card (`data-note`, main.js:27), and
 * the catalog is now gated. Listing it would imply a protection
 * gateScreen() does not apply. */
/* ⚠ `pubnote` ADDED WITH STEP 9, 2026-08-29, AND IT IS NOT OPTIONAL.
 * Ledger §5: login is required to view even a public profile, which is
 * why `pubprofile` is here — and a note-detail screen reached FROM that
 * profile is the same surface one tap deeper. Omitting it would leave a
 * signed-out route to another collector's photographs that the profile
 * it came from refuses. */
const GATED = ['collection', 'catalog', 'community', 'account', 'pubprofile', 'pubnote', 'holding', 'add']

const esc = (s) => String(s ?? '').replace(/[&<>"]/g, (c) => (
  { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]))

/* ── the screens ───────────────────────────────────────────────────── */

/* ⚠ THE DESCRIPTION, his ruling (a) of 2026-10-09, his wording exactly:
 * the signed-out apex is this screen, and Google's branding rule says the
 * home page "can not be only a login page" — it must describe the app. So
 * the description stands above the forms. `search-check` asserts the text
 * is on the signed-out screen; `search-sabotage` SB47 removes it. */
const SIGNIN_DESCRIPTION = 'Currency Keep is where your currency collection lives. Every note in one place, with its photographs and value; a community of collectors whose collections you can browse. Free to use, and what you record is yours.'

/* ⚠ THE SAFARI SENTENCES — register 13448, his ruling (94) D3, the words as
 * shaped. A link from the mail opens in the BROWSER, never in the home-screen
 * app, and the two do not share a sign-in (his walk, 2026-10-08). */
const SAFARI_LINE = 'The link opens in your browser — on an iPhone, that\'s Safari, even if you started in the Currency Keep app on your home screen.'
const RESET_SENT = 'If that address has an account, we\'ve sent it a link to choose a new password. It can take a minute to arrive.'
const BROWSER_CARD = 'You\'re signed in here, in your browser. If you also use Currency Keep from your home screen, open it there and sign in with your email and password — the two don\'t share a sign-in.'
/* ⚠ THE SIGNED-OUT RELOAD — register item, his go 2026-10-18: a boot whose stored session was refused opens
 * this screen with this one sentence, never an empty vault. No promise about the notes: a deleted account
 * refuses the refresh too. `search-check` E1–E4; `search-sabotage` SB70–SB73. */
const SIGNED_OUT_LINE = 'Your sign-in on this device has ended — sign in again to see your vault.'
const SPAM_BOX = `<div style="margin:0 0 12px;padding:10px 12px;border:1px solid var(--line);border-radius:8px;background:var(--card);font-size:13px;line-height:1.5;color:var(--ink)">
        <b>Look in your spam or junk folder first.</b> The first email from a new sender often lands there. If it did, mark it as not spam so the next one reaches your inbox.
      </div>`

/* ⚠ THE HOLD — his ruling (97), B: after a send the reset button is held for
 * a minute for ANY address, and a press inside it makes NO request — so the
 * app never asks the server inside the per-user interval that refuses only
 * a known address. Supabase's own `/recover` still answers the two
 * differently; that is the server's, out of the app's reach. */
const RESET_HOLD_MS = 60_000
const RESET_HELD_LINE = 'You can ask for another link in a minute.'
const resetHeld = () => Date.now() < (state.forgotHoldUntil || 0)
/* the timer that frees the button when the minute is up — redraws the reset
 * screen, and is cleared when the screen is left (his instruction, 2026-10-17) */
let holdTimer = null
function armHold() {
  state.forgotHoldUntil = Date.now() + RESET_HOLD_MS
  if (holdTimer) clearTimeout(holdTimer)
  holdTimer = setTimeout(() => { holdTimer = null; if (state.screen === 'forgot') render() }, RESET_HOLD_MS + 50)
}
function leaveForgot() {
  if (holdTimer) { clearTimeout(holdTimer); holdTimer = null }
  state.screen = 'signin'
  state.forgotSent = false
  state.forgotError = ''
  render()
}

/* The page can tell it is NOT the home-screen app; it cannot tell whether an
 * app icon exists, which is why the card's words are conditional. */
const isStandalone = () => {
  try {
    return globalThis.navigator?.standalone === true || Boolean(globalThis.matchMedia?.('(display-mode: standalone)')?.matches)
  } catch (e) { return false }
}

function vSignIn() {
  return `<div style="padding-top:8px"></div>
    <div class="rowhead"><h2>Sign in</h2></div>
    ${state.signedOut ? `<div class="signin-ended" style="margin:0 2px 14px;padding:10px 12px;border:1px solid var(--line);border-radius:8px;background:var(--card);font-size:13px;line-height:1.5;color:var(--ink)">${SIGNED_OUT_LINE}</div>` : ''}
    <div class="signin-desc" style="font-size:14px;line-height:1.5;color:var(--ink);margin:0 2px 14px">${SIGNIN_DESCRIPTION}</div>
    <div class="acard">
      <div class="acard-h">Your vault</div>
      <div class="fieldhint" style="margin:0 0 12px">Your collection lives on your account, so it is there on every device you sign in from.</div>
      <div class="search" style="margin-bottom:10px">
        <input id="authEmail" type="email" inputmode="email" autocomplete="username" autocapitalize="off" autocorrect="off" placeholder="Email" value="${esc(state.authEmail || '')}">
      </div>
      <div class="search" style="margin-bottom:12px">
        <input id="authPass" type="password" autocomplete="current-password" placeholder="Password">
      </div>
      ${state.authError ? `<div class="fieldhint" style="margin:0 0 12px;color:#9B2C2C;font-size:12px">${esc(state.authError)}</div>` : ''}
      <button class="btn" data-signin ${state.authBusy ? 'disabled style="opacity:.6"' : ''}>${state.authBusy ? 'Signing in…' : 'Sign in'}</button>
      ${state.authUnconfirmed ? `<button class="btn ghost" data-resend-from-signin style="margin-top:10px">Send a new confirmation email</button>` : ''}
      <div style="text-align:center;margin:10px 0 0"><button data-forgot style="background:none;border:0;padding:4px 6px;color:var(--gold-ink);font-size:13px;text-decoration:underline;cursor:pointer">Forgot password?</button></div>
      <div class="fieldhint" style="margin:12px 0 8px;text-align:center">or</div>
      <button class="btn ghost" data-google>Continue with Google</button>
    </div>
    <div class="acard">
      <div class="acard-h">No account yet?</div>
      <div class="fieldhint" style="margin:0 0 12px">Create one here. You will get an email with a link to confirm your address — <b>it may land in your spam or junk folder</b>. The link opens in your browser and signs you in there. Or use <b>Continue with Google</b> above, which needs no confirmation email.</div>
      <div class="search" style="margin-bottom:10px">
        <input id="suEmail" type="email" inputmode="email" autocomplete="username" autocapitalize="off" autocorrect="off" placeholder="Email" value="${esc(state.signupEmail || '')}">
      </div>
      <div class="search" style="margin-bottom:12px">
        <input id="suPass" type="password" autocomplete="new-password" placeholder="Choose a password">
      </div>
      ${state.signupError ? `<div class="fieldhint" style="margin:0 0 12px;color:#9B2C2C;font-size:12px">${esc(state.signupError)}</div>` : ''}
      <button class="btn" data-signup ${state.signupBusy ? 'disabled style="opacity:.6"' : ''}>${state.signupBusy ? 'Creating…' : 'Create account'}</button>
      <div class="fieldhint" style="margin:12px 0 0">How your details are handled is in the <a href="/privacy">privacy policy</a>; the <a href="/terms">terms of use</a> are here.</div>
    </div>
    <div class="hint">The Learn pages are open to everyone.</div>`
}

/**
 * THE CONFIRM SCREEN — Phase 5 step 3. Reached two ways: after a
 * sign-up (the address is known, so it is printed and the field is
 * hidden), and after a confirmation link that could not be used (the
 * fragment carries no address, so the field is shown).
 *
 * ⚠ THE RESEND WORDING IS CONDITIONAL ON PURPOSE. The server accepts a
 * resend for any address, seen or unseen, so the form is not an
 * enumeration oracle — and the sentence on screen says exactly what is
 * true in both cases rather than claiming a mail exists.
 *
 * ⚠ SPAM IS SAID FIRST, IN ITS OWN BOX — his finding from the first real
 * walk (2026-09-10, iPhone, real inbox): the mail landed in spam. The
 * first email from a new sending domain often does, and a person who is
 * told "check spam" as a trailing clause has already stopped reading.
 * The sign-up form says it too, before they go looking.
 */
function vConfirm() {
  const known = state.confirmEmail || ''
  return `<div style="padding-top:8px"></div>
    <div class="rowhead"><h2>Check your email</h2></div>
    <div class="acard">
      <div class="acard-h">${known ? 'One more step' : 'Send a new link'}</div>
      ${known ? `<div style="margin:0 0 12px;padding:10px 12px;border:1px solid var(--line);border-radius:8px;background:var(--card);font-size:13px;line-height:1.5;color:var(--ink)">
        <b>Look in your spam or junk folder first.</b> The first email from a new sender often lands there. If it did, mark it as not spam so the next one reaches your inbox.
      </div>` : ''}
      <div class="fieldhint" style="margin:0 0 12px;font-size:13px;color:var(--ink);line-height:1.5">
        ${known
          ? `We sent a confirmation link to <b>${esc(known)}</b>. It can take a minute to arrive. ${SAFARI_LINE} Once it says you're confirmed, come back to the app and sign in with your email and password.`
          : `Enter the address you signed up with and we will send a fresh confirmation link.`}
      </div>
      ${known ? '' : `<div class="search" style="margin-bottom:12px">
        <input id="cfEmail" type="email" inputmode="email" autocomplete="username" autocapitalize="off" autocorrect="off" placeholder="Email" value="${esc(state.signupEmail || state.authEmail || '')}">
      </div>`}
      ${state.confirmError ? `<div class="fieldhint" style="margin:0 0 12px;color:#9B2C2C;font-size:12px">${esc(state.confirmError)}</div>` : ''}
      ${state.confirmNote ? `<div class="fieldhint" style="margin:0 0 12px;font-size:12px">${esc(state.confirmNote)}</div>` : ''}
      <button class="btn ${known ? 'ghost' : ''}" data-resend ${state.confirmBusy ? 'disabled style="opacity:.6"' : ''}>${state.confirmBusy ? 'Sending…' : (known ? 'Send it again' : 'Send a new link')}</button>
      <button class="btn ghost" data-confirmback style="margin-top:10px">Back to sign in</button>
    </div>`
}

/**
 * THE RESET SEND SCREEN — his ruling (94), 2026-10-17. Reached from the
 * sign-in card's *Forgot password?*, and from a reset link that could not
 * be used (its sentence in `forgotError`, the field showing). After a send
 * the sentence is CONDITIONAL, whatever the address — the server answers
 * alike for an unknown one, so the screen is no account oracle.
 */
function vForgot() {
  return `<div style="padding-top:8px"></div>
    <div class="rowhead"><h2>Reset your password</h2></div>
    <div class="acard">
      ${state.forgotSent ? `${SPAM_BOX}
      <div class="fieldhint forgot-sent" style="margin:0 0 12px;font-size:13px;color:var(--ink);line-height:1.5">${RESET_SENT} ${SAFARI_LINE} Choose your new password there, then come back to the app and sign in with it.</div>` : ''}
      <div class="search" style="margin-bottom:12px">
        <input id="fgEmail" type="email" inputmode="email" autocomplete="username" autocapitalize="off" autocorrect="off" placeholder="Email" value="${esc(state.authEmail || '')}">
      </div>
      ${state.forgotError ? `<div class="fieldhint" style="margin:0 0 12px;color:#9B2C2C;font-size:12px">${esc(state.forgotError)}</div>` : ''}
      <button class="btn" data-sendreset ${state.forgotBusy || resetHeld() ? 'disabled style="opacity:.6"' : ''}>${state.forgotBusy ? 'Sending…' : 'Send reset link'}</button>
      ${resetHeld() ? `<div class="fieldhint reset-held" style="margin:8px 0 0;font-size:12px;text-align:center">${RESET_HELD_LINE}</div>` : ''}
      <button class="btn ghost" data-forgotback style="margin-top:10px">Back to sign in</button>
    </div>`
}

/**
 * THE NEW-PASSWORD SCREEN — his ruling (94). Opened by a reset link's
 * landing, and by EVERY boot while the reset is pending (D1). Two fields,
 * checked equal here; the strength is the server's policy, its reason
 * shown as sign-up shows it. After a save whose sign-out of the other
 * devices failed twice, it stays to say so (his correction, 2026-10-17).
 * ⚠ It offers sign-out, beyond the shape: a pending reset holds every nav
 * tap here, and a screen nobody can leave is the trap `vNoProfile` exists
 * to avoid.
 */
function vNewPass() {
  const email = getSession()?.user?.email || ''
  if (state.newpassWarn) {
    return `<div style="padding-top:8px"></div>
    <div class="rowhead"><h2>Choose a new password</h2></div>
    <div class="acard">
      <div class="fieldhint newpass-warn" style="margin:0 0 12px;font-size:13px;color:var(--ink);line-height:1.5">${esc(state.newpassWarn)}</div>
      <button class="btn" data-newpass-continue>Continue</button>
    </div>`
  }
  return `<div style="padding-top:8px"></div>
    <div class="rowhead"><h2>Choose a new password</h2></div>
    <div class="acard">
      <div class="fieldhint" style="margin:0 0 12px;font-size:13px;color:var(--ink)">For ${esc(email)}.</div>
      <div class="search" style="margin-bottom:10px">
        <input id="npPass1" type="password" autocomplete="new-password" placeholder="New password">
      </div>
      <div class="search" style="margin-bottom:12px">
        <input id="npPass2" type="password" autocomplete="new-password" placeholder="Type it again">
      </div>
      ${state.newpassError ? `<div class="fieldhint newpass-error" style="margin:0 0 12px;color:#9B2C2C;font-size:12px">${esc(state.newpassError)}</div>` : ''}
      <button class="btn" data-savepass ${state.newpassBusy ? 'disabled style="opacity:.6"' : ''}>${state.newpassBusy ? 'Saving…' : 'Save password'}</button>
      <button class="btn ghost" data-signout style="margin-top:10px">Sign out</button>
    </div>`
}

/**
 * THE BROWSER CARD — register 13448, his ruling (94). At the top of the
 * vault after a confirmation or a reset that landed in a browser that is
 * NOT the home-screen app; once per landing, until *Got it*.
 */
function browserCard() {
  if (!state.browserCard || state.screen !== 'collection') return ''
  return `<div class="acard browser-card" style="margin:8px 0 12px">
      <div class="fieldhint" style="margin:0 0 10px;font-size:13px;color:var(--ink);line-height:1.5">${BROWSER_CARD}</div>
      <button class="btn ghost" data-browsercard-ok>Got it</button>
    </div>`
}

function vNoProfile() {
  const id = getSession()?.user?.id || ''
  return `<div style="padding-top:8px"></div>
    <div class="rowhead"><h2>Account</h2></div>
    <div class="acard">
      <div class="acard-h">Something is missing</div>
      <div class="fieldhint" style="margin:0 0 4px;font-size:13px;color:var(--ink);line-height:1.5">
        You are signed in, but this account has no profile attached to it. That is a fault on our side rather than anything you did.
      </div>
      <div class="fieldhint" style="margin:10px 0 14px">Nothing you have saved is affected — Currency Keep simply cannot open your vault until the profile is back. Tap Repair to put it back; it takes a moment.</div>
      ${state.repairError ? `<div class="fieldhint" style="margin:0 0 12px;color:#9B2C2C;font-size:12px">${esc(state.repairError)}</div>` : ''}
      <button class="btn" data-repair ${state.repairBusy ? 'disabled style="opacity:.6"' : ''}>${state.repairBusy ? 'Repairing…' : 'Repair'}</button>
      <button class="btn ghost" data-signout style="margin-top:10px">Sign out</button>
    </div>
    <div class="hint">If Repair does not work, get in touch and quote this account:<br>${esc(id)}</div>`
}

/* Rows appended to the Account screen's Settings card. Lives here rather
 * than in views-account.js because that file is GENERATED — the edit
 * there is one line calling this. */
function authRows() {
  const email = getSession()?.user?.email
  if (!email) return ''
  return `<div class="fieldhint" style="margin:12px 0 0">Signed in as ${esc(email)}</div>
    <button class="btn ghost" data-signout style="margin-top:10px">Sign out</button>`
}

/* ── the gate ──────────────────────────────────────────────────────── */

/**
 * Where a nav tap should actually go.
 *
 * Called from main.js's click handler through an extract REPLACEMENT.
 * Returns the requested screen when there is a session, and the sign-in
 * screen when there is not — rather than letting the app open a Vault
 * it cannot read and showing an empty one.
 */
function gateScreen(target) {
  /* D1 of (94): a pending reset holds every tap on the new-password screen */
  if (isRecoveryPending()) return 'newpass'
  if (isSignedIn()) return target
  return GATED.includes(target) ? 'signin' : target
}

/**
 * True when an action that needs rows may proceed.
 *
 * ⚠ THIS IS WHY `data-addfrom` NEEDS ITS OWN GUARD. Without it, tapping
 * Add on a catalog note while signed out pushes a holding into
 * `state.collection`, fails to save, and then navigates to a holding
 * screen showing a note that does not exist anywhere. The toast would
 * be truthful and the screen would be a lie.
 *
 * ⚠ AND IT STAYS, NOW THAT THE CATALOG IS GATED TOO (2026-08-09). The
 * route it was written for is closed, so this is a second line rather
 * than the only one — but the guard belongs to the ACTION, not to the
 * screen that happens to host it, and a later decision to reopen
 * Resources must not silently reopen the write path with it.
 */
function requireSession() {
  if (isSignedIn()) return true
  state.screen = 'signin'
  render()
  return false
}

/* ── boot ──────────────────────────────────────────────────────────── */

/**
 * Load everything a signed-in session needs, and route the one failure
 * that has no other way of being seen.
 *
 * The profile is probed FIRST and deliberately: `loadProfile()`
 * (community.js:16) is a bare `catch(e){}`, so a missing row is
 * swallowed there and the app carries on into a state it cannot render.
 * This probe costs nothing — `remote.js` caches the profile read, so the
 * `loadProfile()` below is a cache hit rather than a second round trip.
 */
async function bootSession() {
  /* ⚠ D1 OF (94): while a reset is pending, every boot opens the
   * new-password screen and loads nothing else; the save runs this again. */
  if (isRecoveryPending()) {
    state.screen = 'newpass'
    return
  }
  try {
    await window.storage.get(PROFILE_KEY, false)
  } catch (e) {
    if (e?.hint === 'remote.no_profile_row') {
      state.screen = 'noprofile'
      return
    }
    /* Anything else — offline, an expired token — is not this screen's
     * problem. Fall through and let the ordinary load path report it,
     * so one failure is not dressed up as another. */
  }
  /* ⚠ THE SESSION ENDED ON THIS BOOT — register item, his go 2026-10-18. The probe is the first request,
   * so a refused refresh has run by now and cleared the stored session (`auth.js` `refresh()`); before
   * this, `load()` emptied the collection and the vault drew EMPTY with a toast. The test is the session
   * being GONE, never the probe failing: offline keeps the session (the refresh throws, nothing is
   * cleared) and falls through as before. Returns false exactly here; the callers test `=== false`. */
  if (!isSignedIn()) {
    state.screen = 'signin'
    state.signedOut = true
    return false
  }
  state.signedOut = false
  /* ⚠ A READ THAT FAILED FOR ANY REASON BUT A LOST SESSION — offline, an outage, a short read — draws the
   * "didn't load" panel in place of the empty vault (the offline-reload item, his go 2026-10-18); the
   * write guard (`remote.js` `syncVault`) already refuses any save from it. */
  const readErr = await load()
  /* ⚠ THE FRESH-SESSION 401 — his rulings 1(a) and 1(b) of 2026-10-25. A `no_session` read is either a session
   * that truly ended DURING the read (the retry's refresh refused, `auth.js` `refresh()` cleared it) — the
   * sign-in screen with the sentence, as the boot check above — or a refusal of a token still stored (a clock
   * skew on a freshly issued JWT) — the same didn't-load panel, unchanged, where the empty vault and the toast
   * *"Sign in to load your vault"* drew before. `search-check` G6, G7; `search-sabotage` SB108, SB109. */
  if (readErr && readErr.hint === 'remote.no_session' && !isSignedIn()) {
    state.screen = 'signin'
    state.signedOut = true
    return false
  }
  state.vaultUnread = Boolean(readErr)
  await loadProfile()
  await loadCustom()
  /* ══ ⚠⚠⚠ THE COMMUNITY LIST IS PART OF ESTABLISHING A SESSION ══════
   *
   * FOUND 2026-08-27, BY A STRANGER'S ACCOUNT SEEING ONLY THE THREE
   * SEEDED DEMO PROFILES WHILE THE DATABASE WAS ENTIRELY CORRECT.
   *
   * `loadPublic()` had exactly three call sites: `main.js`'s boot, and
   * `publish()`/`unpublish()` — both of which only ever run on the
   * PUBLISHER'S OWN account. So a collector signing in for the first
   * time got their community list built by the BOOT call, which ran
   * BEFORE the session existed: `readCommunity()` needs a token, threw,
   * and `community.js`'s two empty catches swallowed it. `real` came
   * back empty, `state.pubProfiles` became the demo seed, and nothing
   * ever repopulated it — `doSignIn()` calls `bootSession()`, which did
   * not load the community.
   *
   * ⚠ SO EVERY COLLECTOR'S FIRST SESSION SHOWED AN EMPTY COMMUNITY, and
   * it healed itself on the next page load, which is exactly why it
   * would never have been reported. Jesse: *"a public profile a stranger
   * can't read is the feature not working."*
   *
   * ⚠⚠ IT LIVES HERE RATHER THAN IN `doSignIn()` ON PURPOSE. This is the
   * one function every session-establishing path already calls — the
   * signed-in boot and the sign-in action both come through here — so
   * "a session now exists" and "the community reflects it" are the SAME
   * event and a future entry point cannot forget one of them. Putting it
   * in `doSignIn()` would have fixed today's path and left the next one
   * to rediscover this.
   *
   * ⚠ `main.js`'s boot call stays and is now redundant when signed in:
   * `ensureCommunity()` memoises, so the second call is a cache hit, not
   * a second round trip. It still does the work for the signed-out case.
   * Not awaited there, awaited here. */
  await loadPublic()

  /* ══ ⚠⚠⚠ THE PHOTOGRAPH SWEEP — PHASE 4 STEP 7 BULLET 4 ═════════════
   *
   * **FIRED, NOT AWAITED, AND THAT IS A MEASUREMENT RATHER THAN A STYLE
   * CHOICE.** One upload is ~10 s (10,038 ms cold / 10,091 ms warm,
   * measured 2026-08-28 on a real HEIC through the real Function), so a
   * backlog of N is ~10N seconds. `main.js` awaits `bootSession()`
   * BEFORE it calls `render()` — awaiting the sweep here would hold the
   * entire app behind the upload queue and show a collector a blank
   * screen for a minute and a half over eight photographs.
   *
   * ⚠ IT LIVES HERE FOR THE SAME REASON `loadPublic()` DOES, one entry
   * above: this is the one function every session-establishing path
   * calls, so *"a session exists"* and *"the local-only photographs are
   * on their way to the bucket"* are the same event. Putting it in
   * `doSignIn()` would fix today's path and leave the next one to
   * rediscover it — the exact mistake the 2026-08-27 community bug was.
   *
   * ⚠⚠ AND IT NEEDS THE SESSION, which is why it is last: `migrateOne`
   * refuses with `no_session` if the token is not there yet.
   *
   * ⚠ THE REJECTION HANDLER IS NOT DECORATION. An unhandled rejection
   * from a fire-and-forget promise is invisible in production and would
   * make a broken sweep look like an empty one — the shape this whole
   * pass has been closing. It reports to the console; the collector's
   * channel is the toast below and the per-slot badge. */
  sweepPendingPhotos({
    storage: window.storage,
    collection: state.collection,
    /* The transport that reports bytes sent, so a sweep upload shows the
     * same bar on the note as an instant one — 2026-09-13. */
    fetchImpl: xhrUpload,
    /* ⚠ SILENT ON SUCCESS — only a failure speaks. And it says the
     * CONSEQUENCE, not the mechanism: no "R2", no "bucket", no "upload".
     * ⚠⚠ IT MUST NOT SOUND PERMANENT. `photo-view.js` reserves
     * "permanent until re-photographed" for MISSING; a slot that missed
     * this sweep is retried on the next boot, and telling a collector to
     * re-photograph a picture they still have would be the worse lie. */
    onDone: ({ failed }) => toast(
      failed === 1
        ? '1 photograph is still only on this device'
        : `${failed} photographs are still only on this device`
    ),
    /* The badge must clear without a reload, or the state lies in the
     * other direction — a photograph safely in the bucket still marked
     * as local-only. */
    /* ⚠ STEP 4.5b — the boot sweep lands readings too, and they are
     * treated identically. A photograph uploaded on the NEXT boot is
     * still the collector's note, and its suggestion is memory-only
     * (his ruling 5), so it lives exactly as long as this session. */
    onChange: (v) => { applyReading(state, v); render() },
  }).catch((err) => console.error('photo sweep failed', err))

  /* ⚠ THE BRAND SWEEP — Phase 5 client half, 2026-09-07. Same event,
   * same reason, same shape: fired, not awaited, and silent on success.
   * A logo or banner still local from a boot where the upload did not
   * land is retried here; `uploadBrand` renders when it lands and says
   * why on the console when it does not. Nothing reaches the collector,
   * by his rule — the image is already on their screen from the local
   * copy. */
  sweepPendingBrands().catch((err) => console.error('brand sweep failed', err))
}

/**
 * THE LANDING, AT BOOT — Phase 5 step 3. Called by main.js BEFORE it
 * asks `isSignedIn()`. Returns false when the URL carries nothing of
 * ours, so the ordinary boot proceeds untouched. When a link produced a
 * session it runs `bootSession()` itself — the one function every
 * session-establishing path calls, for the reasons written above it —
 * and when the link could not be used it opens the confirm screen with
 * the field showing, since a fragment carries no address.
 */
async function bootFromUrl() {
  let landed = null
  try {
    landed = await landFromUrl()
  } catch (e) {
    landed = { status: 'error', code: 'unexpected', message: 'Could not finish confirming your address. Open the link again.' }
  }
  if (!landed) return false
  /* ⚠ A RESET LINK — his ruling (94): the session is held and pending (D1),
   * and the new-password screen opens, not the vault. */
  if (landed.status === 'recovery') {
    state.screen = 'newpass'
    state.newpassError = ''
    state.newpassWarn = ''
    return true
  }
  if (landed.status === 'signed_in') {
    state.screen = 'collection'
    /* a boot that ended signed out stays on the sign-in screen, with no "signed in" toast */
    if ((await bootSession()) === false) return true
    /* `email` is the token-hash link's type and `signup` the #access_token
     * link's — the same confirmation, the same sentence (his ruling,
     * 2026-10-10). */
    const confirmed = landed.type === 'signup' || landed.type === 'email'
    toast(landed.provider === 'google' ? 'Signed in with Google' : confirmed ? 'Email confirmed — you are signed in' : 'Signed in')
    /* the browser card, register 13448: a confirmation that landed outside the home-screen app */
    if (confirmed && !landed.provider && !isStandalone()) state.browserCard = true
    return true
  }
  /* A reset link that could not be used goes to the RESET form with its
   * sentence — not to the resend-a-confirmation one (his ruling (94)). */
  if (landed.type === 'recovery') {
    state.screen = 'forgot'
    state.forgotSent = false
    state.forgotError = landed.message
    return true
  }
  /* A Google trip that did not finish goes back to the sign-in card
   * with the sentence — not to the resend-a-confirmation form, which
   * is for email links. `landed.provider` is the marker (step 4). */
  if (landed.provider) {
    state.screen = 'signin'
    state.authError = landed.message
    return true
  }
  state.screen = 'confirm'
  state.confirmEmail = ''
  state.confirmNote = ''
  state.confirmError = landed.message
  return true
}

/* ── actions ───────────────────────────────────────────────────────── */

/**
 * The Repair button — Phase 5 step 5. Calls `db/011`'s function through
 * `remote.repairProfile()`, which resolves only with the row the server
 * holds afterwards. On success the ordinary session boot runs — the one
 * function every session-establishing path calls — so the vault opens
 * the way it does after a sign-in. A repair that did not land comes
 * back as `remote.no_profile_row`, and the screen says so and keeps the
 * account id in view for support.
 */
async function doRepair() {
  if (state.repairBusy) return
  state.repairBusy = true
  state.repairError = ''
  render()
  try {
    await repairProfile()
  } catch (e) {
    state.repairBusy = false
    state.repairError = e?.hint === 'remote.no_profile_row'
      ? 'The repair did not take. Please get in touch and quote the account below.'
      : (e?.userMessage || e?.message || 'Could not repair the profile.')
    render()
    return
  }
  state.repairBusy = false
  state.screen = 'collection'
  if ((await bootSession()) === false) { render(); return }
  if (state.screen !== 'noprofile') toast('Your profile is back')
  render()
}

/* The reset send — his ruling (94), and A of (97). Whatever the address, a
 * sent or rate-limited request shows the CONDITIONAL sentence; only what the
 * person can act on (a malformed address, our mailer) shows as an error. */
async function doSendReset() {
  if (state.forgotBusy) return
  /* B of (97): held for a minute after a send — no request, nothing changes */
  if (resetHeld()) return
  const email = ($('fgEmail')?.value ?? state.authEmail ?? '').trim()
  state.authEmail = email
  state.forgotError = ''
  if (!email) {
    state.forgotError = 'Enter your email address.'
    render()
    return
  }
  state.forgotBusy = true
  render()
  try {
    await requestPasswordReset(email)
    state.forgotSent = true
    armHold()
  } catch (e) {
    state.forgotError = e?.message || 'Could not send the email.'
  }
  state.forgotBusy = false
  render()
}

/* The save — his ruling (94): the two fields equal here, the strength the
 * server's; on success the other devices are signed out (D2) and the vault
 * opens; a failed sign-out of them is SAID, on this screen (his correction). */
async function doSavePass() {
  if (state.newpassBusy) return
  const a = $('npPass1')?.value ?? ''
  const b = $('npPass2')?.value ?? ''
  state.newpassError = ''
  if (!a || !b) {
    state.newpassError = 'Type the new password twice.'
    render()
    return
  }
  if (a !== b) {
    state.newpassError = 'The two passwords don\'t match.'
    render()
    return
  }
  state.newpassBusy = true
  render()
  let out
  try {
    out = await setNewPassword(a)
  } catch (e) {
    state.newpassBusy = false
    state.newpassError = e?.message || 'Could not save the password.'
    render()
    return
  }
  state.newpassBusy = false
  if (!out.othersSignedOut) {
    state.newpassWarn = OTHERS_NOT_SIGNED_OUT_MESSAGE
    render()
    return
  }
  await finishNewPass()
}

async function finishNewPass() {
  state.newpassWarn = ''
  state.screen = 'collection'
  if ((await bootSession()) === false) { render(); return }
  toast('Password changed — you are signed in')
  if (!isStandalone()) state.browserCard = true
  render()
}

/* Leaves the page. Nothing to await: the browser comes back to
 * `bootFromUrl` with a fragment, or the person comes back with the
 * Back button and nothing has changed. */
function doGoogle() {
  state.authError = ''
  state.signedOut = false
  try {
    beginGoogleSignIn()
  } catch (e) {
    state.authError = e?.message || 'Could not start Google sign-in.'
    render()
  }
}

async function doSignUp() {
  if (state.signupBusy) return
  state.signedOut = false
  const email = ($('suEmail')?.value ?? '').trim()
  const password = $('suPass')?.value ?? ''
  state.signupEmail = email
  state.signupError = ''

  if (!email || !password) {
    state.signupError = 'Enter an email address and choose a password.'
    render()
    return
  }

  state.signupBusy = true
  render()
  let result
  try {
    result = await signUp(email, password)
  } catch (e) {
    state.signupBusy = false
    state.signupError = e?.message || 'Could not create the account.'
    render()
    return
  }
  state.signupBusy = false

  if (result.status === 'signed_in') {
    /* Only if confirmation is switched off server-side — not the
     * project's setting, but not this screen's to assume either way. */
    state.screen = 'collection'
    await bootSession()
    render()
    return
  }
  state.screen = 'confirm'
  state.confirmEmail = result.email
  state.confirmError = ''
  state.confirmNote = ''
  render()
}

async function doResend() {
  if (state.confirmBusy) return
  const email = (state.confirmEmail || $('cfEmail')?.value || '').trim()
  state.confirmError = ''
  state.confirmNote = ''
  if (!email) {
    state.confirmError = 'Enter your email address.'
    render()
    return
  }
  state.confirmBusy = true
  render()
  try {
    await resendConfirmation(email)
    state.confirmNote = state.confirmEmail
      ? `Sent again to ${email}.`
      : `If ${email} has an account waiting to be confirmed, a new link is on its way.`
  } catch (e) {
    state.confirmError = e?.message || 'Could not send the email.'
  }
  state.confirmBusy = false
  render()
}

/* From the sign-in card, after `email_not_confirmed`: carry the typed
 * address to the confirm screen and send straight away. */
async function doResendFromSignIn() {
  state.screen = 'confirm'
  state.confirmEmail = state.authEmail || ''
  state.confirmError = ''
  state.confirmNote = ''
  state.authUnconfirmed = false
  render()
  await doResend()
}

async function doSignIn() {
  if (state.authBusy) return
  state.signedOut = false
  const email = ($('authEmail')?.value ?? '').trim()
  const password = $('authPass')?.value ?? ''
  state.authEmail = email
  state.authError = ''
  state.authUnconfirmed = false

  if (!email || !password) {
    state.authError = 'Enter your email and password.'
    render()
    return
  }

  state.authBusy = true
  render()
  try {
    await signIn(email, password)
  } catch (e) {
    /* auth.js already reduces this to something safe to show — it
     * deliberately does NOT distinguish "wrong password" from "no such
     * account", because that difference is an account-enumeration
     * oracle and `auth-check` asserts the two messages are identical. */
    state.authBusy = false
    state.authError = e?.message || 'Could not sign in.'
    /* The one failure the person can act on from here: offer the mail
     * again (step 3). `auth.js` carries the code; the message is
     * unchanged and `auth-check` still asserts it. */
    state.authUnconfirmed = e?.code === 'email_not_confirmed'
    render()
    return
  }

  state.authBusy = false
  state.authError = ''
  if ((await bootSession()) === false) { render(); return }
  if (state.screen === 'signin') state.screen = 'collection'
  render()
}

/**
 * Sign out, then RELOAD THE PAGE.
 *
 * ⚠ THE RELOAD IS THE POINT, NOT LAZINESS. `photoCache` (photos.js:15),
 * `brandCache` (community.js) and the community photo cache
 * (`community-photos.js`) are module-level objects with no reset export
 * wired to sign-out. (`pubPhotoCache` was a third until Step 7 bullet 3
 * deleted it, 2026-08-28 — the reload is why its removal changed no
 * sign-out behaviour.) An
 * in-place sign-out leaves one collector's note photos in memory for
 * whoever signs in next on the same device. A reload clears every
 * module-level cache by construction and cannot miss one that gets
 * added later.
 *
 * ⚠ AND BE CLEAR ABOUT WHAT SIGN-OUT CANNOT DO. `signOut()` revokes the
 * refresh token, so no new access token can be minted — but the access
 * token already issued is a stateless JWT and stays valid until it
 * expires, measured at 3600s. Nothing anywhere can revoke it early.
 * This is about the device, not about the server. See BUILD_NOTES.
 */
async function doSignOut() {
  /* ⚠ EVERY UPLOAD IN FLIGHT IS ABORTED FIRST — 2026-09-13. A landing
   * after this must not mutate a collection that belongs to whoever
   * signs in next; the reload below clears the rest by construction. */
  abortAllUploads()
  try {
    await signOut()
  } catch (e) {
    /* signOut clears locally first and unconditionally, so the user is
     * signed out on this device either way. */
  }
  if (typeof location !== 'undefined' && location.reload) location.reload()
}

/* ── wiring ────────────────────────────────────────────────────────── */

document.addEventListener('click', (e) => {
  const t = e.target.closest('[data-signin],[data-signout],[data-signup],[data-google],[data-repair],[data-resend],[data-resend-from-signin],[data-confirmback],[data-forgot],[data-sendreset],[data-forgotback],[data-savepass],[data-newpass-continue],[data-browsercard-ok]')
  if (!t) return
  if ('signin' in t.dataset) doSignIn()
  else if ('signout' in t.dataset) doSignOut()
  else if ('repair' in t.dataset) doRepair()
  else if ('signup' in t.dataset) doSignUp()
  else if ('google' in t.dataset) doGoogle()
  else if ('resend' in t.dataset) doResend()
  else if ('resendFromSignin' in t.dataset) doResendFromSignIn()
  else if ('confirmback' in t.dataset) { state.screen = 'signin'; state.confirmError = ''; state.confirmNote = ''; render() }
  else if ('forgot' in t.dataset) { state.authEmail = ($('authEmail')?.value ?? state.authEmail ?? '').trim(); state.screen = 'forgot'; state.forgotSent = false; state.forgotError = ''; render() }
  else if ('sendreset' in t.dataset) doSendReset()
  else if ('forgotback' in t.dataset) leaveForgot()
  else if ('savepass' in t.dataset) doSavePass()
  else if ('newpassContinue' in t.dataset) finishNewPass()
  else if ('browsercardOk' in t.dataset) { state.browserCard = false; render() }
})

document.addEventListener('keydown', (e) => {
  if (e.key !== 'Enter') return
  if (e.target.id === 'authEmail' || e.target.id === 'authPass') { e.preventDefault(); doSignIn() }
  else if (e.target.id === 'suEmail' || e.target.id === 'suPass') { e.preventDefault(); doSignUp() }
  else if (e.target.id === 'cfEmail') { e.preventDefault(); doResend() }
  else if (e.target.id === 'fgEmail') { e.preventDefault(); doSendReset() }
  else if (e.target.id === 'npPass1' || e.target.id === 'npPass2') { e.preventDefault(); doSavePass() }
})

/* Keep the typed email across the re-render an error causes. The
 * PASSWORD is deliberately not kept: putting it back into a value
 * attribute would write it into the DOM on every render. */
document.addEventListener('input', (e) => {
  if (e.target.id === 'authEmail' || e.target.id === 'fgEmail') state.authEmail = e.target.value
  else if (e.target.id === 'suEmail' || e.target.id === 'cfEmail') state.signupEmail = e.target.value
})

export {
  authRows,
  bootFromUrl,
  bootSession,
  browserCard,
  doSavePass,
  doSendReset,
  gateScreen,
  leaveForgot,
  requireSession,
  vConfirm,
  vForgot,
  vNewPass,
  vNoProfile,
  vSignIn,
}
